import { can, CAPABILITY, isManagerOrAdmin } from "./permissions";

/**
 * Canonical operational projection relation names (DB views).
 * See docs/engineering/operational_projections.md.
 */
export const OPERATIONAL_RELATION = Object.freeze({
  PROJECTS: "projects_operational",
  TIME_LOGS: "time_logs_operational",
  MATERIAL_USAGE: "project_material_usage_operational",
});

export const FINANCIAL_RELATION = Object.freeze({
  PROJECTS: "projects",
  TIME_LOGS: "time_logs",
  MATERIAL_USAGE: "project_material_usage",
});

/**
 * Whether the actor may read cost-bearing project ledger / project financial columns.
 * Missing actor → treat as privileged/internal (base tables); employee screens always pass actor.
 */
export function canAccessProjectFinancialData(actor, orgPolicies) {
  if (!actor) {
    return true;
  }
  return (
    can(actor, CAPABILITY.PROJECT_DETAIL_FINANCIAL, {}, orgPolicies) ||
    isManagerOrAdmin(actor)
  );
}

/**
 * Projects relation for reads: employees use the operational view (no budget/hourly_rate).
 */
export function getProjectsRelationName(actor, orgPolicies) {
  if (canAccessProjectFinancialData(actor, orgPolicies)) {
    return FINANCIAL_RELATION.PROJECTS;
  }
  return OPERATIONAL_RELATION.PROJECTS;
}

/**
 * Labour ledger relation: employees use hours-only operational view.
 */
export function getTimeLogsRelationName(actor, orgPolicies) {
  if (canAccessProjectFinancialData(actor, orgPolicies)) {
    return FINANCIAL_RELATION.TIME_LOGS;
  }
  return OPERATIONAL_RELATION.TIME_LOGS;
}

/**
 * Material usage relation: employees use cost-free operational view.
 */
export function getMaterialUsageRelationName(actor, orgPolicies) {
  if (canAccessProjectFinancialData(actor, orgPolicies)) {
    return FINANCIAL_RELATION.MATERIAL_USAGE;
  }
  return OPERATIONAL_RELATION.MATERIAL_USAGE;
}

/** Operational labour select (no hourly_rate / payroll money fields). */
export const OPERATIONAL_LABOUR_SELECT =
  "id, employee, supabase_user_id, date_iso, total_hours, net_work_seconds, project_id, session_id, is_archived, approval_status, organization_id";

/** Financial labour select (includes rate for Project Summary). */
export const FINANCIAL_LABOUR_SELECT =
  "id, employee, supabase_user_id, date_iso, total_hours, hourly_rate, net_work_seconds, quote_line_snapshot_id";

export function getLabourSelectColumns(actor, orgPolicies) {
  if (canAccessProjectFinancialData(actor, orgPolicies)) {
    return FINANCIAL_LABOUR_SELECT;
  }
  return OPERATIONAL_LABOUR_SELECT;
}
